import { z } from "zod";

enum OrderReason {
  new = "new",
  partial_close = "partial_close",
  early_termination = "early_termination",
}

enum OrderSide {
  Buy = "Buy",
  Sell = "Sell",
}

enum OrderStatus {
  Open = "Open",
  Partial = "Partial",
  Filled = "Filled",
  PendingCancellation = "PendingCancellation",
  Canceled = "Canceled",
  Rejected = "Rejected",
}

enum PricingStatus {
  Pending = "Pending",
  Priced = "Priced",
}

enum OrderTimeInForce {
  GTC = "GTC",
}

enum InstrumentType {
  Option = "option",
  Forward = "forward",
}

enum OrderType {
  Limit = "Limit",
  Market = "Market",
}

const CreateOptionComboSchema = z.object({
  margin_group_id: z.string().uuid(),
  order_type: z.nativeEnum(OrderType),
  size: z.string(),
  legs: z.array(
    z.object({
      instrument_name: z.string(),
      side: z.string(),
      ratio: z.number().optional().default(1),
    })
  ),
  side: z.nativeEnum(OrderSide),
});

const CreateOrderSchema = z.object({
  type: z.nativeEnum(OrderType),
  instrument_name: z.string(),
  size: z.string().min(1), //int
  side: z.nativeEnum(OrderSide),
  margin_group_id: z.string().uuid(),

  // Optional fields
  ratio: z.string().optional(),
  size_unit: z.string().optional(),
  close_only: z.boolean().optional(),
  venue_name: z.string().optional(),
  average_fill_price: z.string().optional(),
  price: z.string().optional(), //int
  time_in_force: z.nativeEnum(OrderTimeInForce).optional(),
  tag: z.string().optional(),
  is_swap: z.boolean().optional(),
  contract_id: z.string().uuid().optional(),
  order_reason: z.nativeEnum(OrderReason).optional(),
  fixed_rate: z.string().optional(), //int
  termination_date: z.string().optional(),
  transaction_id: z.string().uuid().optional(),
  as_of_date: z.string().optional(),
  auto_hedge: z.boolean().optional(),
});

const ManualFillOrderSchema = z.object({
  client_order_id: z.string(),
  average_fill_price: z.string(),
  cumulative_filled_amount: z.string(),
});

const TradeFormSchema = z.object({
  order: CreateOrderSchema,
  pkg: CreateOptionComboSchema,
});

const OrderResourceSchema = z.object({
  id: z.string().uuid(),
  auto_hedge: z.boolean().optional(),
  contract_size: z.string().optional(),
  type: z.nativeEnum(OrderType),
  status: z.nativeEnum(OrderStatus),
  instrument_name: z.string().min(1),
  size_unit: z.string().min(1),
  quote_currency: z.string().nullable().optional(),
  size: z.string().min(1), //int
  side: z.nativeEnum(OrderSide),
  fee: z.string().min(1), //int
  fee_currency: z.string().min(1),
  fee_bps: z.string().min(1), //int
  last_offset: z.number(),
  close_only: z.boolean(),
  filled: z.string().min(1), //int
  margin_group_id: z.string().uuid(),
  update_date: z.string(),
  created_date: z.string().min(1),
  venue_name: z.string(),
  price: z.string().min(1), //int
  time_in_force: z.nativeEnum(OrderTimeInForce),
  tag: z.string(),
  pricing_status: z.nativeEnum(PricingStatus).nullable().optional(),
  average_fill_price: z.string().nullable().optional(),
  is_swap: z.boolean(),
  transaction_id: z.string(),
  swap_details: z
    .object({
      fixed_rate: z.string(),
      termination_date: z.string(),
      contract_id: z.string(),
    })
    .optional(),
  instrument_type: z.string(),
  instrument_id: z.string().uuid(),
});

const PackageOrderResourceSchema = z.object({
  id: z.string().uuid(),
  update_date: z.string(),
  created_date: z.string(),
  created_by: z.string().uuid(),
  margin_group_id: z.string().uuid(),
  order_type: z.nativeEnum(OrderType),
  legs: z.array(
    z.object({
      name: z.string(),
      order_side: z.string(),
      ratio: z.number(),
    })
  ),
  side: z.nativeEnum(OrderSide),
  size: z.string().min(1),
  status: z.nativeEnum(OrderStatus),
  price: z.string(),
  quote_currency_name: z.string(),
  filled_price: z.string(),
  filled_amount: z.string(),
  contract_id: z.string().uuid(),
  order_reason: z.string(),
  client_orders: z.array(
    z.object({
      cumulative_filled_amount: z.string(),
      filled_amount: z.string(),
      average_fill_price: z.string(),
      counter_quantity: z.string(),
      type: z.nativeEnum(OrderType),
      instrument_name: z.string(),
      size: z.string(),
      size_unit: z.string(),
      side: z.nativeEnum(OrderSide),
      close_only: z.boolean(),
      venue_name: z.string(),
      price: z.string(),
      time_in_force: z.nativeEnum(OrderTimeInForce),
      tag: z.string(),
      is_swap: z.boolean(),
      margin_group_id: z.string().uuid(),
      contract_id: z.string().uuid(),
      order_reason: z.string(),
      transaction_id: z.string().uuid(),
      id: z.string().uuid(),
      created_date: z.string(),
      update_date: z.string(),
      status: z.string(),
      quote_currency: z.string(),
      instrument_type: z.nativeEnum(InstrumentType),
      package_order_id: z.string().uuid(),
      as_of_date: z.string(),
      termination_date: z.string(),
    })
  ),
});

type TradeFormResourceType = z.infer<typeof TradeFormSchema>;
type CreateOrderResourceType = z.infer<typeof CreateOrderSchema>;
type OrderResourceType = z.infer<typeof OrderResourceSchema>;
type CreateOptionComboType = z.infer<typeof CreateOptionComboSchema>;
type PackageOrderResourceType = z.infer<typeof PackageOrderResourceSchema>;
type ManualFillOrderType = z.infer<typeof ManualFillOrderSchema>;

export {
  CreateOrderSchema,
  InstrumentType,
  OrderReason,
  OrderResourceSchema,
  OrderSide,
  OrderStatus,
  OrderTimeInForce,
  OrderType,
  PackageOrderResourceSchema,
  PricingStatus,
  TradeFormSchema,
};
export type {
  CreateOptionComboType,
  CreateOrderResourceType,
  ManualFillOrderType,
  OrderResourceType,
  PackageOrderResourceType,
  TradeFormResourceType,
};
